import { useMemo, useState } from "react";
import { Eye, Hospital } from "lucide-react";

import emptyDocs from "assets/images/empty-docs.svg";
import { DataTable } from "components/ui/DataTable/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { usePatientDischargeSummaries } from "features/documents/hooks/usePatientDischargeSummaries";
import {
  describeDischargeSubject,
  DISCHARGE_SUMMARIES_LABEL,
} from "features/documents/utils/dischargeSummary";
import { formatCommonDate } from "features/results/utils/formatters";
import {
  BABY_DISCHARGE_LABEL,
  isBabyDischargeSummary,
} from "features/results/utils/neonatalDischarge";

import styles from "./DischargeSummariesTable.module.css";
import { DischargeSummaryModal } from "./DischargeSummaryModal";

const ROWS_OPTIONS = [10, 20, 50];

const COLUMNS = [
  {
    id: "orderDate",
    header: "Date",
    width: "120px",
    render: (row) => formatCommonDate(row.orderDate),
  },
  {
    id: "subject",
    header: "About",
    width: "190px",
    /*
     * The one column that must not be dropped. Without it a mother's file lists her baby's
     * discharge indistinguishably from her own — the bug KIN-1949 exists to fix.
     */
    render: (row) =>
      isBabyDischargeSummary(row) ? (
        <Pill {...PILL_COLORS.statusWarning}>{BABY_DISCHARGE_LABEL}</Pill>
      ) : (
        describeDischargeSubject(row)
      ),
  },
  {
    id: "messageId",
    header: "Message ID",
    width: "minmax(140px, 1fr)",
    render: (row) => row.messageId ?? "—",
    getTooltipText: (row) => row.messageId ?? "—",
  },
];

export const DischargeSummariesTable = ({ patientId, bare = false }) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [openedSummary, setOpenedSummary] = useState(null);

  const { summaries, totalCount, isLoading, isError } =
    usePatientDischargeSummaries({ patientId, page, rows });

  const rowActions = useMemo(
    () => [
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (row) => setOpenedSummary(row),
      },
    ],
    []
  );

  return (
    <div className={bare ? styles.wrapperBare : styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.title}>
          <Hospital size={20} />
          {DISCHARGE_SUMMARIES_LABEL}
        </p>
      </div>

      <DataTable
        columns={COLUMNS}
        data={summaries}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyIllustration={bare ? undefined : emptyDocs}
        emptyTitle={
          isError
            ? "Error loading discharge summaries"
            : "No discharge summaries found"
        }
        emptyText={
          isError
            ? "Unable to load discharge summaries. Please try again later."
            : "No hospital has sent a discharge summary for this patient."
        }
        currentPage={page}
        rowsPerPage={rows}
        totalCount={totalCount}
        onPageChange={setPage}
        rowsOptions={ROWS_OPTIONS}
        rowsValue={rows}
        onRowsChange={(value) => {
          setRows(value);
          setPage(1);
        }}
        onRowClick={(row) => setOpenedSummary(row)}
        rowActions={rowActions}
      />

      <DischargeSummaryModal
        summary={openedSummary}
        onClose={() => setOpenedSummary(null)}
      />
    </div>
  );
};
